import { ResourceHub, ResourceHubNode } from "@/models/resourceHubs";
import classNames from "classnames";
import React, { useMemo } from "react";
import {
  CommentCountIndicator,
  NodeDescription,
  NodeIcon,
  ResourceHubSortBy,
  getNodeName,
  sortNodesWithFoldersFirst,
} from "turboui";
import { Title } from "../components";
import { useStateWithLocalStorage } from "@/hooks/useStateWithLocalStorage";
import { assertPresent } from "@/utils/assertions";
import { useTranslation } from "react-i18next";

interface Props {
  resourceHub: ResourceHub;
}

export function RegularState(props: Props) {
  const { t } = useTranslation();
  const { name, nodes } = props.resourceHub;
  assertPresent(nodes, "nodes must be present in resourceHub");

  return (
    <div className="flex flex-col h-full">
      <Title title={name === "Documents & Files" ? t("Documents & Files") : name} />

      <div className="bg-surface-dimmed rounded mx-2 flex-1">
        <NodesList nodes={nodes} />
      </div>
    </div>
  );
}

function NodesList({ nodes }: { nodes: ResourceHubNode[] }) {
  const [sortBy] = useStateWithLocalStorage<ResourceHubSortBy>("resourceHub", "sortBy", "name");

  const sortOrder = sortBy === "name" ? "asc" : "desc";
  const sortedNodes = useMemo(() => sortNodesWithFoldersFirst(nodes, sortBy, sortOrder), [nodes, sortBy, sortOrder]);

  return (
    <div>
      {sortedNodes.map((node) => (
        <NodeItem key={node.id} node={node} />
      ))}
    </div>
  );
}

function NodeItem({ node }: { node: ResourceHubNode }) {
  const className = classNames("px-2 py-1.5", "border-b border-stroke-base last:border-b-0", "flex items-center gap-2");
  const commentsCount = node.document?.commentsCount ?? node.file?.commentsCount ?? node.link?.commentsCount ?? 0;

  return (
    <div key={node.id} className={className}>
      <div>
        <NodeIcon node={node} size={32} />
      </div>
      <div className="overflow-hidden leading-snug flex-1">
        <div className="font-bold truncate">{getNodeName(node)}</div>
        <NodeDescription node={node} fontSize="text-[10px] truncate" />
      </div>
      <CommentCountIndicator count={commentsCount} size={16} />
    </div>
  );
}
